import { supabase } from './supabase.js';

export const notificationService = {
  // Save FCM token to database (supports multiple devices)
  async saveToken(fcmToken) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Get family member ID
      const { data: member } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!member) throw new Error('Family member not found');

      // Get device info
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        timestamp: new Date().toISOString()
      };

      // Use the upsert function to handle multiple devices
      const { data: tokenId, error } = await supabase.rpc('upsert_device_token', {
        p_family_member_id: member.id,
        p_fcm_token: fcmToken,
        p_device_info: deviceInfo
      });

      if (error) throw error;
      
      console.log('FCM token saved successfully for device:', tokenId);
      return { success: true, tokenId };
    } catch (error) {
      console.error('Error saving FCM token:', error);
      throw error;
    }
  },

  // Send notification via Edge Function
  async sendNotification({ familyMemberId, title, body, type, priority = 'normal', data = {} }) {
    try {
      // Method 1: Direct Edge Function call
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      console.log('Attempting to call Edge Function with:', { familyMemberId, title, body, type, priority });
      
      const { data: response, error } = await supabase.functions.invoke('send-notification', {
        body: {
          familyMemberId,
          title,
          body,
          type,
          priority,
          data
        }
      });

      console.log('Edge Function response:', { response, error });

      if (error) {
        console.error('Edge function error details:', error);
        console.error('Error context:', error.context);
        console.error('Error status:', error.context?.status);
        
        // Try to get the response body for more details
        if (error.context?.response) {
          try {
            const errorBody = await error.context.response.text();
            console.error('Edge function error body:', errorBody);
          } catch (e) {
            console.error('Could not read error response body');
          }
        }
        
        console.log('Falling back to queue method due to error:', error.message);
        // Fallback to queue method
        return this.queueNotification({ familyMemberId, title, body, type, priority, data });
      }

      return {
        success: true,
        message: 'Notification sent successfully!',
        messageId: response.messageId
      };
    } catch (error) {
      console.error('Error sending notification:', error);
      // Fallback to queue method
      return this.queueNotification({ familyMemberId, title, body, type, priority, data });
    }
  },

  // Queue notification for later processing
  async queueNotification({ familyMemberId, title, body, type, priority = 'normal', data = {} }) {
    try {
      const { data: queueId, error } = await supabase.rpc('queue_notification', {
        p_family_member_id: familyMemberId,
        p_title: title,
        p_body: body,
        p_type: type,
        p_priority: priority,
        p_data: data
      });

      if (error) throw error;

      return {
        success: true,
        message: 'Notification queued for delivery',
        queueId
      };
    } catch (error) {
      console.error('Error queuing notification:', error);
      throw error;
    }
  },

  // Get notification logs for a family member
  async getNotificationLogs(familyMemberId) {
    try {
      const { data, error } = await supabase
        .from('notification_logs')
        .select('*')
        .eq('family_member_id', familyMemberId)
        .order('sent_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error fetching notification logs:', error);
      throw error;
    }
  }
};